/**
 * Client-generierte IDs (UUID v4) sind die Basis der Idempotenz: Erzeuger (PWA/Kasse)
 * vergibt die ID, der Server dedupliziert danach. Mehrfaches Senden eines Events ist
 * dadurch harmlos.
 *
 * crypto.randomUUID() ist in Bun, modernen Browsern und secure contexts verfügbar.
 */

export type Uuid = string & { readonly __brand: "Uuid" };

export function newId(): Uuid {
  return uuidv4() as Uuid;
}

/**
 * UUID v4 mit Fallback: `crypto.randomUUID()` gibt es NUR im secure context
 * (HTTPS/localhost) — auf dem Handy über `http://<LAN-IP>` ist es undefined und würde
 * die App beim Start abstürzen lassen. `getRandomValues` ist auch unsicher verfügbar.
 */
function uuidv4(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();

  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6]! & 0x0f) | 0x40; // Version 4
  b[8] = (b[8]! & 0x3f) | 0x80; // Variante
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0"));
  return `${h.slice(0, 4).join("")}-${h.slice(4, 6).join("")}-${h.slice(6, 8).join("")}-${h.slice(8, 10).join("")}-${h.slice(10, 16).join("")}`;
}

// Branded ID-Typen je Entität — verhindert das Verwechseln von z. B. OrderId und MenuItemId.
export type VenueId = Uuid & { readonly __entity: "Venue" };
export type MenuCategoryId = Uuid & { readonly __entity: "MenuCategory" };
export type MenuItemId = Uuid & { readonly __entity: "MenuItem" };
export type ModifierGroupId = Uuid & { readonly __entity: "ModifierGroup" };
export type ModifierId = Uuid & { readonly __entity: "Modifier" };
export type TableId = Uuid & { readonly __entity: "Table" };
export type OrderId = Uuid & { readonly __entity: "Order" };
export type OrderLineId = Uuid & { readonly __entity: "OrderLine" };
export type PaymentId = Uuid & { readonly __entity: "Payment" };
export type UserId = Uuid & { readonly __entity: "User" };
export type DeviceId = Uuid & { readonly __entity: "Device" };
export type PrinterId = Uuid & { readonly __entity: "Printer" };
export type EventId = Uuid & { readonly __entity: "Event" };

// Plattform-/Mandanten-Ebene (Cloud-Registry, vom Admin-Portal verwaltet).
export type KundeId = Uuid & { readonly __entity: "Kunde" };
export type ServerId = Uuid & { readonly __entity: "Server" };
export type GeraetId = Uuid & { readonly __entity: "Geraet" };
export type ModulId = Uuid & { readonly __entity: "Modul" };
export type BuchungId = Uuid & { readonly __entity: "Buchung" };
export type AccountId = Uuid & { readonly __entity: "Account" };
